import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import type { ArticlePreviewCardProps } from '~/components/atoms/ArticlePreviewCard/ArticlePreviewCard'
import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import Container from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import Image from '~/components/atoms/Image/Image'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'
import { toURL } from '~/utils/urlResolver'

import ArticleAuthor from '../ArticleAuthor/ArticleAuthor'

import styles from './CategoryCalloutArticles.module.css'

type CalloutItem = {
  title: string
  theme: 'secondary' | 'primary'
  article: ArticlePreviewCardProps
  imageOnLeft?: boolean
}

export interface CategoryCalloutArticlesProps {
  testID?: string

  calloutArticles?: CalloutItem[]
}

export default function CategoryCalloutArticles({
  testID,
  calloutArticles,
}: CategoryCalloutArticlesProps) {
  const t = useTranslations('common')
  const getUrl = (article: ArticlePreviewCardProps) => {
    const urlObj: any = {
      slug: article.slug,
      articleTaxonomy: article.articleTaxonomy,
      _type: 'article',
    }

    if (article.articleType) {
      urlObj.parent = {
        _type: article.articleType,
        slug: article.artistSlug ? article.artistSlug : article.articleTypeSlug,
      }
    }
    return toURL(urlObj)
  }

  const isPodcastOrVideo = (article: ArticlePreviewCardProps) => {
    const isVideo = ['video', 'videos'].includes(article.articleTypeSlug || '')
    const isPodcast = ['podcast', 'podcasts'].includes(
      article.articleTypeSlug || ''
    )
    const isPodcastOrVideo = isVideo || isPodcast

    return isPodcastOrVideo
  }

  return (
    <div className={styles.base}>
      {calloutArticles &&
        calloutArticles?.length > 0 &&
        calloutArticles?.map((category: CalloutItem) => (
          <Fixture
            theme={
              category?.theme
                ? ComponentTheme[category?.theme]
                : ComponentTheme.secondary
            }
            key={`category-callout-article-${category.article.slug}`}
          >
            <Container>
              <div className={styles.articleItemContainer}>
                <div className={styles.titleContainer}>
                  <Text
                    size={TextSizes.primaryTitleMed}
                    className={styles.title}
                  >
                    {category?.title}
                  </Text>
                </div>
                <div
                  className={classNames(styles.categoryItem, {
                    [styles.reverse]: !category.imageOnLeft,
                  })}
                  key={`category-list-sub-item-${category.article.slug}`}
                >
                  {/* Sits left */}
                  <div className={styles.imageContainer}>
                    {category?.article?.leadImage?.asset && (
                      <Image
                        asset={
                          category?.article?.listPreviewImage?.asset ||
                          category?.article?.leadImage?.asset
                        }
                        alt={`${category?.title}`}
                      />
                    )}
                  </div>

                  {/* Sits right */}
                  <div className={styles.articleContent}>
                    <div className={styles.articleContentInner}>
                      <Text
                        className={styles.articleTitle}
                        size={TextSizes.secondaryTitleMed}
                      >
                        {category.article.title}
                      </Text>
                      {(!isPodcastOrVideo(category.article) ||
                        (isPodcastOrVideo(category.article) &&
                          category.article.authorDetails?.name)) && (
                        <div className={styles.authorContainer}>
                          <ArticleAuthor
                            author={category.article.author}
                            centered={false}
                            preText={
                              isPodcastOrVideo(category.article)
                                ? t('featuring')
                                : t('writtenBy')
                            }
                            size={undefined}
                          />
                        </div>
                      )}
                      {category.article.summary && (
                        <Text
                          className={styles.summary}
                          size={TextSizes.bodyMed}
                        >
                          {category.article.summary}
                        </Text>
                      )}
                      <Button
                        variant={ButtonVariants.primaryDark}
                        iconLeft
                        button={{
                          type: ButtonTypes.button,
                        }}
                        link={getUrl(category.article)}
                        className={styles.ctaLink}
                      >
                        <div
                          dangerouslySetInnerHTML={{
                            __html: t('readMore'),
                          }}
                        />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Container>
          </Fixture>
        ))}
    </div>
  )
}
